import {
    startRegistration,
    startAuthentication,
    browserSupportsWebAuthn,
    platformAuthenticatorIsAvailable,
} from '@simplewebauthn/browser';
import axiosApi from '../apiServices/api/axiosAPI';
import { apiConstants } from '../apiServices/constants/apiConstants';

export class PasskeyError extends Error {
    constructor(message, code = 'api') {
        super(message);
        this.name = 'PasskeyError';
        this.code = code;
    }
}

function authHeaders(token, { timeout = 30000, skipSessionExpiry = false } = {}) {
    const bearer = typeof token === 'string' ? token.trim() : '';
    if (!bearer || bearer === 'undefined' || bearer === 'null' || Array.isArray(token)) {
        return { timeout, skipSessionExpiry };
    }
    return { 
        headers: {
            Authorization: `Bearer ${bearer}`,
            'Content-Type': 'application/json',
        },
        timeout,
        skipSessionExpiry,
    };
}

function jsonHeaders({ timeout = 30000, skipSessionExpiry = false } = {}) {
    return {
        headers: {
            'Content-Type': 'application/json',
        },
        timeout,
        skipSessionExpiry,
    };
}

function getPasskeyOrigin() {
    if (typeof window === 'undefined') return '';
    return window.location.origin;
}

function normalizePasskeyUser(value) {
    return String(value || '').trim().toLowerCase();
}

function isMissingValue(value) {
    if (value == null) return true;
    if (Array.isArray(value)) return value.length === 0;
    const raw = String(value).trim();
    return !raw || raw === 'undefined' || raw === 'null' || raw === '0';
}

export function pickPasskeyUserId(source) {
    if (source == null || source === '') return '';
    if (typeof source !== 'object') {
        return isMissingValue(source) ? '' : String(source).trim();
    }
    const value =
        source.user_id ??
        source.UserID ??
        source.UserId ??
        source.userid ??
        source.login_user_id ??
        source.LoginUserID ??
        source.LoginUserId ??
        source.Table?.[0]?.user_id ??
        source.Table?.[0]?.UserID ??
        source.table?.[0]?.user_id ??
        source.table?.[0]?.UserID;
    if (isMissingValue(value)) return '';
    return String(value).trim();
}

export const OFFER_PASSKEY_AFTER_LOGIN_KEY = 'pmr.offerPasskeyAfterLogin';
const PASSKEY_CREDENTIAL_IDS_KEY = 'pmr.passkeyCredentialIds';

function rememberPasskeyCredentialId(email, credentialId) {
    const key = normalizePasskeyUser(email);
    const id = toBase64Url(credentialId || '');
    if (!key || !id || typeof localStorage === 'undefined') return;
    try {
        const raw = localStorage.getItem(PASSKEY_CREDENTIAL_IDS_KEY);
        const all = raw ? JSON.parse(raw) : {};
        const prev = Array.isArray(all[key]) ? all[key] : [];
        const next = [id, ...prev.map((item) => toBase64Url(item)).filter(Boolean)].filter(
            (item, index, arr) => arr.indexOf(item) === index
        );
        all[key] = next.slice(0, 10);
        localStorage.setItem(PASSKEY_CREDENTIAL_IDS_KEY, JSON.stringify(all));
    } catch {
        // ignore quota / private mode
    }
}

let passkeyOfferQueued = false;

export function markPasskeyOfferAfterLogin() {
    passkeyOfferQueued = true;
    try {
        sessionStorage.setItem(OFFER_PASSKEY_AFTER_LOGIN_KEY, '1');
    } catch {
        // ignore storage failures
    }
}

export function hasPasskeyOfferAfterLogin() {
    if (passkeyOfferQueued) return true;
    try {
        return sessionStorage.getItem(OFFER_PASSKEY_AFTER_LOGIN_KEY) === '1';
    } catch {
        return false;
    }
}

export function clearPasskeyOfferAfterLogin() {
    passkeyOfferQueued = false;
    try {
        sessionStorage.removeItem(OFFER_PASSKEY_AFTER_LOGIN_KEY);
    } catch {
        // ignore storage failures
    }
}

export const SETUP_PASSKEY_FROM_URL_KEY = 'pmr.setupPasskeyFromUrl';
export const OPEN_PASSKEY_SECURITY_KEY = 'pmr.openPasskeySecurity';

function readSetupPasskeyIntent() {
    try {
        const raw = sessionStorage.getItem(SETUP_PASSKEY_FROM_URL_KEY);
        if (!raw) return { setupPasskey: false, email: '' };
        const parsed = JSON.parse(raw);
        return {
            setupPasskey: Boolean(parsed?.setupPasskey),
            email: normalizePasskeyUser(parsed?.email || ''),
        };
    } catch {
        return { setupPasskey: false, email: '' };
    }
}

function writeSetupPasskeyIntent(intent) {
    try {
        if (!intent?.setupPasskey) {
            sessionStorage.removeItem(SETUP_PASSKEY_FROM_URL_KEY);
            return;
        }
        sessionStorage.setItem(
            SETUP_PASSKEY_FROM_URL_KEY,
            JSON.stringify({
                setupPasskey: true,
                email: normalizePasskeyUser(intent.email || ''),
            })
        );
    } catch {
        // ignore storage failures
    }
}

function clearOpenPasskeySecurityFlag() {
    try {
        sessionStorage.removeItem(OPEN_PASSKEY_SECURITY_KEY);
    } catch {
        // ignore storage failures
    }
}

/** Persist ?setupPasskey=1&email=... from the URL, then clean the query string. */
export function rememberPasskeySetupFromUrl() {
    if (typeof window === 'undefined') {
        return { setupPasskey: false, email: '' };
    }

    const params = new URLSearchParams(window.location.search);
    const setupPasskey = params.get('setupPasskey') === '1';
    const email = normalizePasskeyUser(params.get('email') || '');

    if (setupPasskey) {
        const intent = { setupPasskey: true, email };
        writeSetupPasskeyIntent(intent);
        try {
            sessionStorage.setItem(OPEN_PASSKEY_SECURITY_KEY, '1');
        } catch {
            // ignore storage failures
        }
        params.delete('setupPasskey');
        params.delete('email');
        const query = params.toString();
        const next = `${window.location.pathname}${query ? `?${query}` : ''}${window.location.hash || ''}`;
        window.history.replaceState({}, document.title, next);
        return intent;
    }

    return readSetupPasskeyIntent();
}

export function shouldOpenPasskeySecurity() {
    try {
        if (sessionStorage.getItem(OPEN_PASSKEY_SECURITY_KEY) === '1') return true;
    } catch {
        // ignore storage failures
    }
    return readSetupPasskeyIntent().setupPasskey;
}

/**
 * Confirm the logged-in user matches a pending setupPasskey intent.
 * Clears the open/setup flags once a decision is made (except when still pending login).
 */
export function confirmPasskeySetupForLoggedInUser(loggedInUser) {
    const intent = readSetupPasskeyIntent();
    const current = normalizePasskeyUser(loggedInUser);

    if (!current) {
        return { ok: false, reason: 'pending' };
    }

    clearOpenPasskeySecurityFlag();
    writeSetupPasskeyIntent({ setupPasskey: false, email: '' });

    if (intent.email && intent.email !== current) {
        return { ok: false, reason: 'user-mismatch' };
    }

    return { ok: true };
}

export function pickPasskeyToken(source) {
    if (!source || typeof source !== 'object') return '';
    const value =
        source.Token ||
        source.token ||
        source.access_token ||
        source.AccessToken ||
        source.Table?.[0]?.Token ||
        source.Table?.[0]?.token ||
        source.table?.[0]?.Token ||
        source.table?.[0]?.token;
    if (isMissingValue(value) || Array.isArray(value)) return '';
    return String(value).trim();
}

function getSessionEmail(session) {
    if (!session || typeof session !== 'object') return '';
    return normalizePasskeyUser(
        session.user_name ||
        session.UserName ||
        session.email_address ||
        session.EmailAddress ||
        session.email ||
        session.Email
    );
}

export function normalizeLoginSession(payload) {
    if (!payload || typeof payload !== 'object') return null;
    const row = payload.Table?.[0] || payload.table?.[0] || payload.Result || payload.result;
    const nested = row && typeof row === 'object' && !Array.isArray(row) && (
        row.Token || row.token || row.user_id || row.UserID || row.user_name || row.UserName
    )
        ? row
        : {};
    const source = { ...payload, ...nested };
    const token = pickPasskeyToken(source);
    const userId = pickPasskeyUserId(source);
    const user_name = source.user_name || source.UserName || nested.user_name || nested.UserName || '';
    const email_address = source.email_address || source.EmailAddress || nested.email_address || user_name;
    return {
        ...source,
        Token: token,
        token,
        user_id: userId,
        UserID: userId,
        user_name,
        email_address,
        user_type_name: source.user_type_name || source.UserTypeName || nested.user_type_name,
        menu_items: source.menu_items || source.menuItems || source.Table1 || payload.Table1 || [],
        family_accounts: source.family_accounts || source.familyAccounts || source.Table2 || payload.Table2 || [],
    };
}

function passkeyUserPayload({ userName, emailAddress, userId } = {}) {
    const user_name = normalizePasskeyUser(userName || emailAddress);
    const email_address = normalizePasskeyUser(emailAddress || userName);
    const id = pickPasskeyUserId(userId);
    const numericId = /^\d+$/.test(id) ? Number(id) : id;
    return {
        user_name,
        email_address,
        origin: getPasskeyOrigin(),
        ...(id
            ? {
                user_id: numericId,
                UserID: numericId,
                userId: numericId,
            }
            : {}),
    };
}

function looksLikePasskeyRow(item) {
    if (!item || typeof item !== 'object' || Array.isArray(item)) return false;
    const credentialId = item.credential_id || item.CredentialId || item.credentialId;
    if (credentialId != null && String(credentialId).trim() !== '') return true;
    return Boolean(
        item.public_key ||
        item.PublicKey ||
        item.device_name ||
        item.DeviceName ||
        item.aaguid ||
        item.Aaguid
    );
}

function extractPasskeyRows(payload) {
    if (!payload) return [];
    const rawLists = [];
    if (Array.isArray(payload)) rawLists.push(payload);
    const candidates = [
        payload.passkeys,
        payload.Passkeys,
        payload.Table,
        payload.table,
        payload.Table1,
        payload.data,
        payload.Data,
    ];
    for (const candidate of candidates) {
        if (!Array.isArray(candidate) || candidate.length === 0) continue;
        const first = candidate[0];
        const nested = first?.passkeys || first?.Passkeys || first?.Table;
        if (Array.isArray(nested) && nested.length) {
            rawLists.push(nested);
            continue;
        }
        rawLists.push(candidate);
    }
    for (const list of rawLists) {
        const rows = list.filter(looksLikePasskeyRow);
        if (rows.length) return rows;
    }
    return [];
}

function asObject(value) {
    if (!value) return null;
    if (typeof value === 'string') {
        try {
            return JSON.parse(value);
        } catch {
            return null;
        }
    }
    return typeof value === 'object' ? value : null;
}

function toBase64Url(value) {
    if (value == null || value === '') return value;
    if (typeof value !== 'string') return value;
    return value.replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function utf8ToBase64Url(text) {
    const bytes = new TextEncoder().encode(String(text));
    let binary = '';
    bytes.forEach((byte) => {
        binary += String.fromCharCode(byte);
    });
    return btoa(binary).replace(/\+/g, '-').replace(/\//g, '_').replace(/=+$/g, '');
}

function decodedByteLength(base64Url) {
    try {
        const normalized = toBase64Url(String(base64Url || ''));
        if (!normalized) return 0;
        const pad = '='.repeat((4 - (normalized.length % 4)) % 4);
        return atob(normalized.replace(/-/g, '+').replace(/_/g, '/') + pad).length;
    } catch {
        return 0;
    }
}

function isEncodedUserHandle(value) {
    if (!/^[A-Za-z0-9_-]+$/.test(value) || value.length < 4) return false;
    if (/^\d+$/.test(value)) return false;
    if (/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(value)) return false;
    const size = decodedByteLength(value);
    return size >= 1 && size <= 64;
}

function toWebAuthnUserId(value, fallbackUserId) {
    const candidate = value == null || value === '' ? fallbackUserId : value;
    if (candidate == null || candidate === '') return utf8ToBase64Url('acme-user');
    const trimmed = String(candidate).trim();
    if (!trimmed || trimmed === 'undefined' || trimmed === 'null') {
        return fallbackUserId ? utf8ToBase64Url(String(fallbackUserId)) : utf8ToBase64Url('acme-user');
    }
    if (isEncodedUserHandle(trimmed)) return toBase64Url(trimmed);
    return utf8ToBase64Url(trimmed);
}

const DEFAULT_PUB_KEY_PARAMS = [
    { type: 'public-key', alg: -7 },
    { type: 'public-key', alg: -257 },
];

function sanitizePubKeyCredParams(params) {
    const source = Array.isArray(params) ? params : [];
    const normalized = [];
    const seen = new Set();
    source.forEach((item) => {
        const alg = Number(item?.alg ?? item?.Alg);
        if (!Number.isInteger(alg) || seen.has(alg)) return;
        seen.add(alg);
        normalized.push({
            type: item?.type || item?.Type || 'public-key',
            alg,
        });
    });
    DEFAULT_PUB_KEY_PARAMS.forEach((item) => {
        if (seen.has(item.alg)) return;
        seen.add(item.alg);
        normalized.push(item);
    });
    return normalized;
}

function sanitizeCredentialDescriptors(list) {
    if (!Array.isArray(list)) return [];
    return list.map((item) => {
        const id = toBase64Url(item?.id || item?.Id || item?.credential_id || item?.CredentialId || '');
        if (!id || decodedByteLength(id) < 16) return null;
        const descriptor = {
            type: item?.type || item?.Type || 'public-key',
            id,
        };
        const transports = item?.transports || item?.Transports;
        if (Array.isArray(transports) && transports.length) {
            descriptor.transports = transports;
        }
        return descriptor;
    }).filter(Boolean);
}

function resolveRpId(apiRpId) {
    const host = typeof window !== 'undefined' ? window.location.hostname : '';
    const requested = String(apiRpId || '').trim().toLowerCase();
    if (!host) return requested || undefined;
    if (!requested || requested === host) return host;
    if (host === requested || host.endsWith(`.${requested}`)) return requested;
    return host;
}

function pickChallenge(options) {
    return options?.challenge || options?.Challenge || '';
}

function sanitizeWebAuthnOptions(raw, { forRegistration = false } = {}) {
    const source = asObject(raw) || raw;
    if (!source || typeof source !== 'object') return null;

    const challenge = toBase64Url(pickChallenge(source));
    if (!challenge) return null;

    const userSource = source.user || source.User;
    const rpSource = source.rp || source.Rp;
    const excludeSource = source.excludeCredentials || source.ExcludeCredentials || [];
    const allowRaw = source.allowCredentials || source.AllowCredentials || source.allow_credentials || [];
    const allowSource = Array.isArray(allowRaw)
        ? allowRaw
        : (Array.isArray(asObject(allowRaw)) ? asObject(allowRaw) : []);

    const options = {
        rp: {
            id: resolveRpId(rpSource?.id || rpSource?.Id),
            name: rpSource?.name || rpSource?.Name || 'Acme Health',
        },
        user: {
            id: toWebAuthnUserId(userSource?.id || userSource?.Id),
            name: userSource?.name || userSource?.Name,
            displayName: userSource?.displayName || userSource?.DisplayName || userSource?.name || userSource?.Name,
        },
        challenge,
        pubKeyCredParams: sanitizePubKeyCredParams(source.pubKeyCredParams || source.PubKeyCredParams),
        timeout: Math.max(Number(source.timeout || source.Timeout) || 0, 180000),
        attestation: 'none',
        authenticatorSelection: {
            userVerification: 'preferred',
            residentKey: 'preferred',
            requireResidentKey: false,
        },
        excludeCredentials: sanitizeCredentialDescriptors(excludeSource),
        allowCredentials: sanitizeCredentialDescriptors(allowSource),
    };

    if (forRegistration) {
        // Phone browser: this account is already signed in here, so use that phone.
        // Computer: ask Windows for the phone QR screen directly. Setting
        // authenticatorAttachment makes Windows try a local save first, which
        // shows "Something went wrong" before the QR code.
        const onPhone = isMobileBrowser();
        if (onPhone) {
            options.authenticatorSelection.authenticatorAttachment = 'platform';
            options.hints = ['client-device'];
        } else {
            delete options.authenticatorSelection.authenticatorAttachment;
            options.authenticatorSelection.residentKey = 'required';
            options.authenticatorSelection.requireResidentKey = true;
            options.authenticatorSelection.userVerification = 'required';
            options.hints = ['hybrid'];
        }
        delete options.allowCredentials;
        if (!options.excludeCredentials?.length) delete options.excludeCredentials;
        if (!options.rp?.id) return null;
    } else {
        delete options.rp;
        delete options.user;
        delete options.pubKeyCredParams;
        delete options.attestation;
        delete options.excludeCredentials;
        delete options.authenticatorSelection;
        options.userVerification = 'preferred';
        // Hybrid only: Windows opens the phone QR code instead of the
        // "iPhone, iPad, or Android device" / "Security key" chooser.
        options.hints = ['hybrid'];
        delete options.allowCredentials;
        options.rpId = resolveRpId(source.rpId || source.RpId || rpSource?.id || rpSource?.Id);
    }

    Object.keys(options).forEach((key) => {
        if (options[key] === undefined) delete options[key];
    });

    return options;
}

export function unwrapPasskeyOptions(payload, { forRegistration = false } = {}) {
    const root = payload?.data ?? payload;
    const parsedRoot = asObject(root) || root;
    if (!parsedRoot) return null;

    const candidates = [
        parsedRoot.optionsJSON,
        parsedRoot.OptionsJSON,
        parsedRoot.options,
        parsedRoot.Options,
        parsedRoot.publicKey,
        parsedRoot.PublicKey,
        parsedRoot.data?.optionsJSON,
        parsedRoot.data?.options,
        parsedRoot,
    ];

    for (const candidate of candidates) {
        const options = sanitizeWebAuthnOptions(asObject(candidate) || candidate, { forRegistration });
        if (options?.challenge) return options;
    }

    const tableRow = parsedRoot.Table?.[0] || parsedRoot.table?.[0];
    if (tableRow) return unwrapPasskeyOptions(tableRow, { forRegistration });

    return null;
}

function isMobileBrowser() {
    const os = getOsName();
    return os === 'iPhone' || os === 'iPad' || os === 'Android';
}

function bytesFromBase64Url(value) {
    try {
        const normalized = toBase64Url(String(value || ''));
        if (!normalized) return new Uint8Array();
        const pad = '='.repeat((4 - (normalized.length % 4)) % 4);
        const binary = atob(normalized.replace(/-/g, '+').replace(/_/g, '/') + pad);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i += 1) bytes[i] = binary.charCodeAt(i);
        return bytes;
    } catch {
        return new Uint8Array();
    }
}

function aaguidFromAuthData(authData) {
    if (!authData || authData.length < 53) return '';
    const attested = (authData[32] & 0x40) !== 0;
    if (!attested) return '';
    const hex = Array.from(authData.slice(37, 53), (byte) => byte.toString(16).padStart(2, '0')).join('');
    return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

function authDataFromAttestationObject(attestationObject) {
    const bytes = bytesFromBase64Url(attestationObject);
    const marker = [0x68, 0x61, 0x75, 0x74, 0x68, 0x44, 0x61, 0x74, 0x61];
    for (let i = 0; i <= bytes.length - marker.length - 3; i += 1) {
        let match = true;
        for (let j = 0; j < marker.length; j += 1) {
            if (bytes[i + j] !== marker[j]) {
                match = false;
                break;
            }
        }
        if (!match) continue;
        const lenByte = bytes[i + marker.length];
        let start = 0;
        let length = 0;
        if (lenByte === 0x58) {
            length = bytes[i + marker.length + 1];
            start = i + marker.length + 2;
        } else if (lenByte === 0x59) {
            length = (bytes[i + marker.length + 1] << 8) | bytes[i + marker.length + 2];
            start = i + marker.length + 3;
        } else {
            continue;
        }
        if (length >= 37 && start + length <= bytes.length) {
            return bytes.slice(start, start + length);
        }
    }
    return null;
}

function readPasskeyAaguid(credential) {
    const direct = credential?.response?.authenticatorData;
    if (direct) {
        const fromDirect = aaguidFromAuthData(bytesFromBase64Url(direct));
        if (fromDirect) return fromDirect;
    }
    const fromAttestation = authDataFromAttestationObject(credential?.response?.attestationObject);
    return aaguidFromAuthData(fromAttestation);
}

const PHONE_PASSKEY_AAGUIDS = {
    'ea9b8d66-4d01-1d21-3ce4-b6b48cb575d4': { device_name: 'Android phone', os: 'Android', browser: 'Chrome' },
    'fbfc3007-154e-4ecc-8c0b-6e020557d7bd': { device_name: 'iPhone', os: 'iPhone', browser: 'Safari' },
    'dd4ec289-e01d-41c9-bb89-70fa845d8bf2': { device_name: 'iPhone', os: 'iPhone', browser: 'Safari' },
    'c5778178-0391-4b7e-a574-4cbe1a1c6224': { device_name: 'iPhone', os: 'iPhone', browser: 'Safari' },
};

function getCredentialTransports(credential) {
    let listed = credential?.response?.transports || credential?.transports || [];
    if ((!Array.isArray(listed) || listed.length === 0) && typeof credential?.response?.getTransports === 'function') {
        try {
            listed = credential.response.getTransports() || [];
        } catch {
            listed = [];
        }
    }
    return (Array.isArray(listed) ? listed : []).map((item) => String(item).toLowerCase());
}

function isPhoneQrCredential(credential) {
    const transports = getCredentialTransports(credential);
    if (transports.includes('hybrid') || transports.includes('cable')) return true;
    const attachment = String(credential?.authenticatorAttachment || '').toLowerCase();
    return attachment === 'cross-platform' && transports.length === 0;
}

function passkeyDeviceProfile(credential) {
    const aaguid = readPasskeyAaguid(credential);
    const knownPhone = PHONE_PASSKEY_AAGUIDS[aaguid];
    if (isPhoneQrCredential(credential)) {
        if (knownPhone) return { ...knownPhone, aaguid };
        return { device_name: 'Phone', os: 'Phone', browser: '', aaguid };
    }
    return {
        device_name: getDeviceLabel(),
        os: getOsName(),
        browser: getBrowserName(),
        aaguid,
    };
}

function toFido2Attestation(credential) {
    return {
        id: credential?.id,
        rawId: credential?.rawId || credential?.id,
        type: credential?.type || 'public-key',
        extensions: credential?.clientExtensionResults || credential?.extensions,
        response: {
            attestationObject: credential?.response?.attestationObject,
            clientDataJSON: credential?.response?.clientDataJSON,
            transports: credential?.response?.transports,
        },
    };
}

function toFido2Assertion(credential) {
    return {
        id: credential?.id,
        rawId: credential?.rawId || credential?.id,
        type: credential?.type || 'public-key',
        extensions: credential?.clientExtensionResults || credential?.extensions,
        response: {
            authenticatorData: credential?.response?.authenticatorData,
            clientDataJSON: credential?.response?.clientDataJSON,
            signature: credential?.response?.signature,
            userHandle: credential?.response?.userHandle,
        },
    };
}

export function getPasskeyApiError(error, fallback = 'Passkey request failed. Please try again.') {
    if (error instanceof PasskeyError) return error.message;
    const data = error?.response?.data;
    return (
        data?.ErrorMessage ||
        data?.errorMessage ||
        data?.Message ||
        data?.message ||
        (typeof data === 'string' ? data : null) ||
        error?.cause?.message ||
        error?.message ||
        fallback
    );
}

export function isPasskeyCancelled(error) {
    if (!error) return false;
    if (error.code === 'cancelled') return true;

    const name = String(error.name || error.cause?.name || '');
    const code = String(error.code || error.cause?.code || '');
    if (
        name === 'NotAllowedError' ||
        name === 'AbortError' ||
        code === 'ERROR_CEREMONY_ABORTED'
    ) {
        return true;
    }

    const message = String(error.message || error.cause?.message || '').toLowerCase();
    return (
        message.includes('timed out or was not allowed') ||
        message.includes('privacy-considerations-client') ||
        message.includes('the operation was aborted') ||
        message.includes('user cancelled') ||
        message.includes('was cancelled')
    );
}

export function isPasskeySupported() {
    try {
        return typeof window !== 'undefined' && browserSupportsWebAuthn();
    } catch {
        return false;
    }
}

export async function isPlatformPasskeyAvailable() {
    try {
        if (!isPasskeySupported()) return false; 
        return await platformAuthenticatorIsAvailable();
    } catch {
        return false;
    }
}

export function getPasskeySupportMessage() {
    if (typeof window === 'undefined') {
        return 'Passkeys are not available in this environment.';
    }
    if (!window.isSecureContext) {
        return 'Passkeys require a secure connection (HTTPS or localhost).';
    }
    if (!browserSupportsWebAuthn()) {
        return 'This browser does not support passkeys. Try Chrome, Edge, or Safari.';
    }
    return '';
}

export function getBrowserName() {
    const ua = navigator.userAgent || '';
    const brands = navigator.userAgentData?.brands;
    if (Array.isArray(brands) && brands.length) {
        const label = (value) => String(value?.brand || '');
        if (brands.some((item) => /edge/i.test(label(item)))) return 'Edge';
        if (brands.some((item) => /opera/i.test(label(item)))) return 'Opera';
        if (brands.some((item) => /chrome/i.test(label(item)) && !/not|chromium/i.test(label(item)))) {
            return 'Chrome';
        }
    }
    if (/Edg\//i.test(ua)) return 'Edge';
    if (/OPR\/|Opera/i.test(ua)) return 'Opera';
    if (/Firefox\//i.test(ua)) return 'Firefox';
    if (/Chrome\//i.test(ua) && !/Edg\//i.test(ua)) return 'Chrome';
    if (/Safari/i.test(ua) && !/Chrome/i.test(ua)) return 'Safari';
    return 'Browser';
}

export function getOsName() {
    const ua = navigator.userAgent || '';
    const platform = navigator.userAgentData?.platform || navigator.platform || '';
    if (/iPhone/i.test(ua)) return 'iPhone';
    if (/iPad/i.test(ua) || (platform === 'MacIntel' && navigator.maxTouchPoints > 1)) return 'iPad';
    if (/Android/i.test(ua)) return 'Android';
    if (/Mac/i.test(platform) || /Mac OS/i.test(ua)) return 'Mac';
    if (/Win/i.test(platform) || /Windows/i.test(ua)) return 'Windows';
    return 'this device';
}

export function getDeviceLabel() {
    return `${getBrowserName()} on ${getOsName()}`;
}

export function getCurrentDeviceFriendlyName() {
    const os = getOsName();
    if (os === 'iPhone' || os === 'Android') return 'This phone';
    if (os === 'iPad') return 'This tablet';
    return 'This computer';
}

export function formatPasskeyDeviceName(value) {
    const raw = String(value || '').trim();
    if (!raw || /^this device$/i.test(raw)) return getDeviceLabel();
    if (/^windows hello$/i.test(raw)) {
        const platform = navigator.userAgentData?.platform || navigator.platform || navigator.userAgent || '';
        if (/Win/i.test(platform) || /Windows/i.test(platform)) {
            return `${getBrowserName()} on Windows`;
        }
        return 'Windows computer';
    }
    if (/^iphone \/ ipad/i.test(raw)) return `${getBrowserName()} on ${getOsName()}`;
    if (/^android \(/i.test(raw)) return `${getBrowserName()} on Android`;
    if (/^mac \(/i.test(raw)) return `${getBrowserName()} on Mac`;
    return raw;
}

export function nextPasskeyDeviceName(existingNames = []) {
    const base = getDeviceLabel();
    const taken = new Set(
        (existingNames || []).map((name) => formatPasskeyDeviceName(name).trim().toLowerCase())
    );
    if (!taken.has(base.toLowerCase())) return base;
    let index = 2;
    while (taken.has(`${base} (${index})`.toLowerCase())) {
        index += 1;
    }
    return `${base} (${index})`;
}

function assertPasskeyEnvironment() {
    const message = getPasskeySupportMessage();
    if (message) {
        throw new PasskeyError(message, window?.isSecureContext ? 'unsupported' : 'insecure');
    }
}

function isDocumentFocused() {
    return typeof document !== 'undefined'
        && document.visibilityState === 'visible'
        && document.hasFocus();
}

function isPageFocusError(error) {
    const message = String(error?.message || error?.cause?.message || '').toLowerCase();
    return message.includes('page does not have focus') || message.includes('document does not have focus');
}

async function waitForDocumentFocus(timeoutMs = 20000) {
    if (typeof window === 'undefined') return;
    try {
        window.focus();
    } catch {
        // browsers may block this
    }
    if (isDocumentFocused()) return;

    await new Promise((resolve, reject) => {
        const timer = setTimeout(() => {
            cleanup();
            reject(new PasskeyError(
                'Keep this browser tab in front, then click Enable Passkey again. Chrome cannot open Windows Hello while DevTools or another window is focused.',
                'focus'
            ));
        }, timeoutMs);

        const onReady = () => {
            if (!isDocumentFocused()) return;
            cleanup();
            resolve();
        };

        function cleanup() {
            clearTimeout(timer);
            window.removeEventListener('focus', onReady);
            document.removeEventListener('visibilitychange', onReady);
        }

        window.addEventListener('focus', onReady);
        document.addEventListener('visibilitychange', onReady);
    });
}

async function runWebAuthnWhenFocused(action) {
    await waitForDocumentFocus();
    try {
        return await action();
    } catch (error) {
        if (!isPageFocusError(error)) throw error;
        await waitForDocumentFocus();
        return action();
    }
}

function pickNamedArray(data, names) {
    if (!data || typeof data !== 'object') return null;
    for (const name of names) {
        const value = data[name];
        if (Array.isArray(value) && value.length > 0) return value;
    }
    return null;
}

function looksLikeMenuRow(row) {
    return Boolean(
        row &&
        (row.menu_id != null ||
            row.Menu_Id != null ||
            row.MenuId != null ||
            row.title != null ||
            row.menu_name != null)
    );
}

function extractAuthSession(data) {
    const session = normalizeLoginSession(data);
    if (!session?.Token) return null;
    return session;
}

function throwIfApiError(data) {
    const status = String(data?.status || data?.Status || '').toLowerCase();
    const message = data?.ErrorMessage || data?.errorMessage || data?.Message || data?.message;
    if (status && status !== 'ok' && message) {
        throw new PasskeyError(message, 'api');
    }
    if (!message || typeof message !== 'string') return;
    const lower = message.toLowerCase();
    const isError =
        Boolean(data?.ErrorMessage) ||
        (Boolean(data?.errorMessage) && lower !== '') ||
        lower.includes('no passkey') ||
        lower.includes('not allowed') ||
        lower.includes('failed') ||
        lower.includes('error') ||
        lower.includes('not found') ||
        lower.includes('no http resource');
    if (isError) {
        throw new PasskeyError(message, 'api');
    }
}

export async function registerPasskey({ userName, emailAddress, userId, token, deviceName } = {}) {
    assertPasskeyEnvironment();
    const email = normalizePasskeyUser(userName || emailAddress);
    const resolvedUserId = pickPasskeyUserId(userId);
    const resolvedToken = typeof token === 'string' ? token.trim() : pickPasskeyToken({ Token: token, token });
    if (!email) {
        throw new PasskeyError('Email is required to create a passkey.', 'validation');
    }
    if (!resolvedToken) {
        throw new PasskeyError('Please sign in before adding a passkey.', 'unauthorized');
    }
    if (!resolvedUserId) {
        throw new PasskeyError('Could not create a passkey because your user id was missing. Please sign in again.', 'validation');
    }

    const userPayload = passkeyUserPayload({ userName: email, emailAddress: email, userId: resolvedUserId });
    const beginResp = await axiosApi.post(
        apiConstants.login.passkeyRegisterBegin,
        userPayload,
        authHeaders(resolvedToken, { skipSessionExpiry: true })
    );
    throwIfApiError(beginResp.data);

    const optionsJSON = unwrapPasskeyOptions(beginResp.data, { forRegistration: true });
    if (!optionsJSON?.challenge || !optionsJSON?.rp?.id) {
        throw new PasskeyError('Could not start passkey setup. The API options were incomplete.', 'api');
    }
    optionsJSON.user = {
        ...(optionsJSON.user || {}),
        id: toWebAuthnUserId(optionsJSON.user?.id, resolvedUserId),
        name: email,
        displayName: email,
    };

    let credential;
    try {
        credential = await runWebAuthnWhenFocused(() => startRegistration({ optionsJSON }));
    } catch (error) {
        if (error instanceof PasskeyError) throw error;
        if (isPageFocusError(error)) {
            throw new PasskeyError(
                'Keep this Chrome tab in front (close or undock DevTools), then click Enable Passkey again.',
                'focus'
            );
        }
        if (isPasskeyCancelled(error)) {
            throw new PasskeyError('Passkey setup was cancelled.', 'cancelled');
        }
        throw new PasskeyError(getPasskeyApiError(error, 'Could not create a passkey on this device. Keep Bluetooth on, keep this tab in front, then try again.'), 'webauthn');
    }

    const attestation = toFido2Attestation(credential);
    const deviceProfile = passkeyDeviceProfile(credential);
    rememberPasskeyCredentialId(email, attestation.id || attestation.rawId);
    const completeResp = await axiosApi.post(
        apiConstants.login.passkeyRegisterComplete,
        {
            ...userPayload,
            ...attestation,
            credential: attestation,
            attestationResponse: attestation,
            credential_json: JSON.stringify(attestation),
            device_name: deviceProfile.device_name || deviceName || getDeviceLabel(),
            browser: deviceProfile.browser || getBrowserName(),
            os: deviceProfile.os || getOsName(),
            aaguid: deviceProfile.aaguid || '',
        },
        authHeaders(resolvedToken, { skipSessionExpiry: true })
    );
    throwIfApiError(completeResp.data);
    clearPasskeyListCache(resolvedUserId, email);
    rememberAccountHasPasskey(email, true);
    return completeResp.data;
}

export async function authenticateWithPasskey(userName) {
    assertPasskeyEnvironment();
    const email = normalizePasskeyUser(userName);
    if (!email) {
        throw new PasskeyError('Enter your email, then tap Sign in with Passkey.', 'validation');
    }

    const userPayload = passkeyUserPayload({ userName: email, emailAddress: email });
    const beginResp = await axiosApi.post(
        apiConstants.login.passkeyLoginBegin,
        userPayload,
        jsonHeaders({ skipSessionExpiry: true })
    );
    throwIfApiError(beginResp.data);

    const optionsJSON = unwrapPasskeyOptions(beginResp.data, { forRegistration: false });
    if (!optionsJSON?.challenge) {
        throw new PasskeyError('Could not start passkey sign-in. Please try again.', 'api');
    }

    // QR code first. Do not offer the phone/security-key chooser or a local Windows Hello row.
    optionsJSON.hints = ['hybrid'];
    optionsJSON.userVerification = optionsJSON.userVerification || 'preferred';
    delete optionsJSON.allowCredentials;

    let credential;
    try {
        credential = await runWebAuthnWhenFocused(() => startAuthentication({ optionsJSON }));
    } catch (error) {
        if (error instanceof PasskeyError) throw error;
        if (isPageFocusError(error)) {
            throw new PasskeyError(
                'Keep this Chrome tab in front (close or undock DevTools), then click Sign in with Passkey again.',
                'focus'
            );
        }
        if (isPasskeyCancelled(error)) {
            throw new PasskeyError('Passkey sign-in was cancelled.', 'cancelled');
        }
        throw new PasskeyError(getPasskeyApiError(error, 'Could not verify this device.'), 'webauthn');
    }

    const assertion = toFido2Assertion(credential);
    const completeResp = await axiosApi.post(
        apiConstants.login.passkeyLoginComplete,
        {
            ...userPayload,
            ...assertion,
            credential: assertion,
            assertionResponse: assertion,
            credential_json: JSON.stringify(assertion),
        },
        jsonHeaders({ skipSessionExpiry: true })
    );
    throwIfApiError(completeResp.data);

    const session = extractAuthSession(completeResp.data);
    if (!session?.Token) {
        throw new PasskeyError(
            completeResp.data?.ErrorMessage ||
            completeResp.data?.errorMessage ||
            'Passkey was verified, but the API did not return a login token from PasskeyLoginComplete.',
            'api'
        );
    }
    rememberPasskeyCredentialId(email, assertion.id || assertion.rawId);
    rememberAccountHasPasskey(email, true);
    const sessionEmail = getSessionEmail(session);
    if (sessionEmail && sessionEmail !== email) {
        throw new PasskeyError(
            'This passkey belongs to a different account. Enter that email, or create a passkey while signed in as yourself.',
            'wrong-user'
        );
    }
    return session;
}

const passkeyAvailabilityCache = new Map();
const passkeyAvailabilityInflight = new Map();

function rememberAccountHasPasskey(email, hasPasskey) {
    const key = normalizePasskeyUser(email);
    if (!key) return;
    passkeyAvailabilityCache.set(key, Boolean(hasPasskey));
}

function passkeyAbsenceMessage(value) {
    const text = String(value || '').toLowerCase();
    return (
        text.includes('no passkey') ||
        text.includes('passkey not') ||
        text.includes('does not have a passkey') ||
        text.includes('without a passkey') ||
        text.includes('no credential') ||
        text.includes('not registered')
    );
}

function findAllowCredentialCount(data) {
    let found = null;
    const visit = (node, depth) => {
        if (node == null || depth > 6) return;
        if (typeof node === 'string') {
            const parsed = asObject(node);
            if (parsed && typeof parsed === 'object') visit(parsed, depth + 1);
            return;
        }
        if (typeof node !== 'object') return;
        if (Array.isArray(node)) {
            node.forEach((item) => visit(item, depth + 1));
            return;
        }
        const allow = node.allowCredentials ?? node.AllowCredentials ?? node.allow_credentials;
        if (Array.isArray(allow)) {
            found = Math.max(found ?? 0, allow.length);
        }
        const count = node.passkey_count ?? node.PasskeyCount ?? node.passkeyCount ?? node.PasskeysCount;
        if (count != null && count !== '' && !Number.isNaN(Number(count))) {
            found = Math.max(found ?? 0, Number(count));
        }
        Object.values(node).forEach((child) => {
            if (child && typeof child === 'object') visit(child, depth + 1);
        });
    };
    visit(data, 0);
    return found;
}

function beginPayloadHasPasskey(data) {
    const message = data?.ErrorMessage || data?.errorMessage || data?.Message || data?.message || '';
    if (passkeyAbsenceMessage(message)) return false;
    try {
        throwIfApiError(data);
    } catch {
        return false;
    }
    if (findAllowCredentialCount(data) > 0) return true;
    const options = unwrapPasskeyOptions(data, { forRegistration: false });
    return Boolean(options?.challenge);
}

export function peekAccountHasPasskey(userName) {
    const email = normalizePasskeyUser(userName);
    if (!passkeyAvailabilityCache.has(email)) return null;
    return passkeyAvailabilityCache.get(email);
}

/** True only when PasskeyLoginBegin confirms this email already has a passkey. */
export async function accountHasPasskey(userName) {
    const email = normalizePasskeyUser(userName);
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) return false;
    if (passkeyAvailabilityCache.has(email)) return passkeyAvailabilityCache.get(email);
    if (passkeyAvailabilityInflight.has(email)) return passkeyAvailabilityInflight.get(email);

    const request = axiosApi.post(
        apiConstants.login.passkeyLoginBegin,
        passkeyUserPayload({ userName: email, emailAddress: email }),
        jsonHeaders({ timeout: 12000, skipSessionExpiry: true })
    ).then((resp) => {
        const hasPasskey = beginPayloadHasPasskey(resp.data);
        passkeyAvailabilityCache.set(email, hasPasskey);
        return hasPasskey;
    }).catch((error) => {
        const message = getPasskeyApiError(error, '');
        if (error?.response || passkeyAbsenceMessage(message)) {
            passkeyAvailabilityCache.set(email, false);
        }
        return false;
    }).finally(() => {
        passkeyAvailabilityInflight.delete(email);
    });

    passkeyAvailabilityInflight.set(email, request);
    return request;
}

const passkeyListCache = new Map();
const passkeyListInflight = new Map();

function passkeyListKey(userId, userName) {
    return `${pickPasskeyUserId(userId) || ''}|${normalizePasskeyUser(userName)}`;
}

export function peekPasskeyList(userId, userName) {
    return passkeyListCache.get(passkeyListKey(userId, userName)) || null;
}

export function clearPasskeyListCache(userId, userName) {
    passkeyListCache.delete(passkeyListKey(userId, userName));
}

async function requestPasskeyList({ token, userId, userName, emailAddress } = {}) {
    const options = authHeaders(token, { timeout: 15000, skipSessionExpiry: true });
    const id = pickPasskeyUserId(userId);
    const numericId = /^\d+$/.test(String(id)) ? Number(id) : id;
    const body = {
        user_name: normalizePasskeyUser(userName || emailAddress),
        email_address: normalizePasskeyUser(emailAddress || userName),
        origin: getPasskeyOrigin(),
        ...(id ? { user_id: numericId } : {}),
    };
    const readRows = (resp) => {
        try {
            throwIfApiError(resp.data);
        } catch (error) {
            const message = String(error?.message || '').toLowerCase();
            if (message.includes('no passkey') || message.includes('not found')) return [];
            throw error;
        }
        return extractPasskeyRows(resp.data);
    };

    try {
        const postResp = await axiosApi.post(apiConstants.login.passkeysList, body, options);
        return readRows(postResp);
    } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403) return [];
        if (status && status !== 404 && status !== 405 && status !== 400) throw error;
    }

    try {
        const getResp = await axiosApi.get(apiConstants.login.passkeysList, {
            ...options,
            params: id ? { user_id: numericId } : undefined,
        });
        return readRows(getResp);
    } catch (error) {
        const status = error?.response?.status;
        if (status === 401 || status === 403 || status === 404) return [];
        throw error;
    }
}

export async function listPasskeys({ token, userId, userName, emailAddress, force = false } = {}) {
    if (!token) {
        throw new PasskeyError('Please sign in to view passkeys.', 'unauthorized');
    }
    const key = passkeyListKey(userId, userName || emailAddress);
    if (!force && passkeyListCache.has(key)) {
        return passkeyListCache.get(key);
    }
    if (!force && passkeyListInflight.has(key)) {
        return passkeyListInflight.get(key);
    }

    const request = requestPasskeyList({ token, userId, userName, emailAddress })
        .then((rows) => {
            passkeyListCache.set(key, rows);
            return rows;
        })
        .finally(() => {
            passkeyListInflight.delete(key);
        });
    passkeyListInflight.set(key, request);
    return request;
}

export async function deletePasskey({ token, credentialId, userId, userName, emailAddress } = {}) {
    if (!token) {
        throw new PasskeyError('Please sign in to remove a passkey.', 'unauthorized');
    }
    if (!credentialId) {
        throw new PasskeyError('Select a passkey to remove.', 'validation');
    }
    const resp = await axiosApi.post(
        apiConstants.login.passkeyDelete,
        {
            ...passkeyUserPayload({ userId, userName, emailAddress: emailAddress || userName }),
            credential_id: credentialId,
            CredentialId: credentialId,
            credentialId,
        },
        authHeaders(token, { skipSessionExpiry: true })
    );
    throwIfApiError(resp.data);
    clearPasskeyListCache(userId, userName || emailAddress);
    return resp.data;
}
